import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { CalendarPlusIcon, VideoIcon } from 'lucide-react';
import { useCatalog } from '../../contexts/CatalogContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { useSignedInAccount } from '../../contexts/AccountContext';
import { founder } from '../../data/content';
import { policyCopy } from '../../data/policies';
import { useSite } from '../../contexts/SiteContext';
import { courseStatus, dayShort, newestFirst, upcomingAppointments } from '../../utils/account';
import { btnPrimary, btnQuiet, btnSecondary } from '../../ui/styles';
import { accountHeading } from '../../ui/sections';
import { ease } from '../../core/format/motion';
import { PageTitle } from './PageTitle';
import { EmptyCard } from './EmptyCard';
import { CourseProgress } from './cards/CourseProgress';
import { AppointmentCard } from './cards/AppointmentCard';

/**
 * Salon: de kuur (als die er is) bovenaan met een eigen plek, dan de komende afspraken
 * (in agenda · verzetten · annuleren) en de eerdere bezoeken, elk met "Zelfde nog eens".
 */
export function Appointments() {
  const {
    policy
  } = useSite();
  const {
    account,
    rescheduleAppointment,
    cancelAppointment
  } = useSignedInAccount();
  const {
    treatmentBySlug,
    routineForTreatment
  } = useCatalog();
  const {
    openBooking
  } = useOverlayActions();
  const reduce = useReducedMotion();
  const upcoming = upcomingAppointments(account.appointments);
  const cs = courseStatus(account);
  const course = cs?.course;
  const courseT = course ? treatmentBySlug(course.treatmentSlug) : undefined;
  const nextSession = cs && !cs.finished ? cs.nextToBook : undefined;
  const aftercare = cs?.finished && course ? routineForTreatment(course.treatmentSlug) : undefined;
  const visits = newestFirst(account.visits);
  return <div data-id="element-5803" data-mp="1gr05h07rtz6r">
      <PageTitle lead={`${policyCopy.cancel}, hier op je scherm.`} action={upcoming.length > 0 && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={btnPrimary} data-id="element-5805" data-mp="w3irvjfegk1z">
              <CalendarPlusIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5806" data-mp="r5qqlysv7ll4" />
              Nieuwe afspraak
            </button>} data-id="element-5804" data-mp="1gr9vgnstg6cg" />

      <div className="space-y-12" data-id="element-5807" data-mp="11355htz542bb">
        {course && courseT && <section aria-labelledby="course-title" className="bg-sand px-5 py-6 sm:px-8 sm:py-7" data-id="element-5808" data-mp="111txtj2kqaex">
            <h2 id="course-title" className={accountHeading} data-id="element-5809" data-mp="1esp3ynloudet">
              {cs?.finished ? 'Kuur afgerond' : 'Je kuur'}
            </h2>
            <div className="mt-4 max-w-xl" data-id="element-5810" data-mp="1osfw4ims0gbq">
              <CourseProgress course={course} bookedSessions={cs?.booked ?? []} name={courseT.name} data-id="element-5811" data-mp="1os7k0syh9f1l" />
            </div>
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-muted" data-id="element-5812" data-mp="1jrjte8e5v07w">
              {cs?.finished ? `Alle sessies zitten erop. Een onderhoudssessie houdt het resultaat vast${aftercare ? `; thuis doet het ${aftercare.name} dat elke dag` : ''}.` : `Het mooiste resultaat krijg je met een sessie ${policyCopy.courseSpacing}. Plan ze gerust alvast in.`}
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-id="element-5813" data-mp="263uxvmpg4ygi">
              {nextSession ? <button type="button" onClick={() => openBooking(course.treatmentSlug)} aria-haspopup="dialog" className={btnPrimary} data-id="element-5814" data-mp="1rb1is2e36kcd">
                  Plan sessie {nextSession}
                </button> : cs?.finished ? <>
                  <button type="button" onClick={() => openBooking(course.treatmentSlug)} aria-haspopup="dialog" className={btnPrimary} data-id="element-5815" data-mp="1mf3zbn7j0xvd">
                    Plan een onderhoudssessie
                  </button>
                  {aftercare && <Link to="/routines" className={btnSecondary} data-id="element-5816" data-mp="1df7j89z7d7bx">
                      Bekijk het {aftercare.name}
                    </Link>}
                </> : null}
            </div>
          </section>}

        <section aria-labelledby="upcoming-title" data-id="element-5817" data-mp="17o371a0oan0e">
          <h2 id="upcoming-title" className={upcoming.length ? `${accountHeading} mb-4` : 'sr-only'} data-id="element-5818" data-mp="17ms2h7zelb88">
            Komende afspraken
          </h2>
          {upcoming.length === 0 ? <EmptyCard className="max-w-2xl" icon={<CalendarPlusIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-5820" data-mp="tobp4y71cmh4" />} title={visits.length ? 'Geen afspraak ingepland.' : `Welkom bij ${founder.firstName}.`} actions={<>
                  <button type="button" onClick={() => openBooking(nextSession && courseT ? course?.treatmentSlug : undefined)} aria-haspopup="dialog" className={btnPrimary} data-id="element-5821" data-mp="jy8vcvs3b4hq">
                    {nextSession && courseT ? `Plan sessie ${nextSession}` : 'Behandeling plannen'}
                  </button>
                  <button type="button" onClick={() => openBooking(policy.consult.slug)} aria-haspopup="dialog" className={btnSecondary} data-id="element-5822" data-mp="5z0rvqow9a7q">
                    <VideoIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-5823" data-mp="2cob5wdvpbj1d" />
                    Online huidcheck
                  </button>
                </>} data-id="element-5819" data-mp="yqki3ujwroj8">
              {visits.length ? `Plan je volgende behandeling, of kies een online huidcheck van ${policy.consult.minutes} minuten.` : `Nog nooit in de salon geweest? Begin met een behandeling, of eerst online: in ${policy.consult.minutes} minuten kijkt ${founder.firstName} met je mee.`}
            </EmptyCard> : <div className="space-y-4" data-id="element-5824" data-mp="1ywwcdd463zg2">
              <AnimatePresence initial={false} data-id="element-5825" data-mp="22yhupdzh15xn">
                {upcoming.map(a => {
              const treatment = treatmentBySlug(a.treatmentSlug);
              return <motion.div key={a.id} layout={!reduce} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={reduce ? {
                opacity: 0
              } : {
                opacity: 0,
                height: 0
              }} transition={{
                duration: 0.22,
                ease
              }} data-id="element-5826" data-mp="1b7212zzhyixx">
                      <AppointmentCard appointment={a} name={treatment?.name ?? a.treatmentSlug} minutes={treatment?.durationMinutes} onMove={(date, time) => rescheduleAppointment(a.id, date, time)} onCancel={() => cancelAppointment(a.id)} data-id="element-5827" data-mp="2djyhgqkscb2g" />
                    </motion.div>;
            })}
              </AnimatePresence>
            </div>}
        </section>

        {visits.length > 0 && <section aria-labelledby="visits-title" className="max-w-3xl" data-id="element-5828" data-mp="24jjwjsbqgxzh">
            <h2 id="visits-title" className={accountHeading} data-id="element-5829" data-mp="3k0jylva8izu8">
              Eerder bij {founder.firstName}
            </h2>
            <ul className="mt-4 divide-y divide-line border-y border-line" data-id="element-5830" data-mp="3fzqcn7gcrv3z">
              {visits.map(v => {
            const name = treatmentBySlug(v.treatmentSlug)?.name ?? v.treatmentSlug;
            return <li key={`${v.treatmentSlug}-${v.date}`} className="flex items-center gap-3 py-3" data-id="element-5831" data-mp="3sicblt747ezb">
                    <span className="min-w-0 flex-1" data-id="element-5832" data-mp="3dluwqrzpvi80">
                      <span className="block break-words text-[15px] text-ink" data-id="element-5833" data-mp="3ij4yn74almcq">
                        {name}
                        {v.session ? <span className="text-muted" data-id="element-5834" data-mp="a4mjklfxp6jm"> · sessie {v.session}</span> : null}
                      </span>
                      <span className="block text-[13px] tabular-nums text-muted" data-id="element-5835" data-mp="f24ughdihvu9">{dayShort(v.date)}</span>
                    </span>
                    {/* Een kuursessie plan je via de kuur hierboven; los boeken zou de telling missen. */}
                    {!v.session && <button type="button" onClick={() => openBooking(v.treatmentSlug)} aria-haspopup="dialog" aria-label={`${name} nog eens boeken`} className={`${btnQuiet} shrink-0`} data-id="element-5836" data-mp="t1e9hdpzejwn">
                        Zelfde nog eens
                      </button>}
                  </li>;
          })}
            </ul>
          </section>}
      </div>
    </div>;
}